import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import { Eye, EyeOff } from 'lucide-react';
import Logo from './Logo';
import './SignUp.css';

const SignUp = () => {
    const navigate = useNavigate();
    const { signup } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validate form
        if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        // Password validation
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            setLoading(false);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (!acceptTerms) {
            setError('Please accept the terms and conditions');
            setLoading(false);
            return;
        }

        try {
            const success = await signup(formData.name, formData.email, formData.password, formData.company);
            if (success) {
                navigate('/');
            } else {
                setError('An account with this email already exists');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        try {
            setError('');
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/`,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                }
            });

            if (error) {
                console.error('Google signup error:', error.message);
                setError('Failed to sign up with Google. Please try again.');
            }
        } catch (err) {
            console.error('Unexpected error:', err);
            setError('An unexpected error occurred');
        }
    };

    const handleGithubSignup = async () => {
        try {
            setError('');
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'github',
                options: {
                    redirectTo: `${window.location.origin}/`,
                }
            });

            if (error) {
                console.error('GitHub signup error:', error.message);
                setError('Failed to sign up with GitHub. Please try again.');
            }
        } catch (err) {
            console.error('Unexpected error:', err);
            setError('An unexpected error occurred');
        }
    };

    return (
        <div className="signup-container">
            <div className="signup-background">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
            </div>

            <div className="signup-content">
                <div className="signup-side">
                    <div className="side-content">
                        <h2>Start your journey today</h2>
                        <p>Manage your customer relationships efficiently with our comprehensive CRM solution.</p>

                        <div className="benefits-list">
                            <div className="benefit-item">
                                <span className="benefit-icon">🚀</span>
                                <div>
                                    <h4>Quick Setup</h4>
                                    <p>Get started in minutes, not hours</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon">🔒</span>
                                <div>
                                    <h4>Secure & Private</h4>
                                    <p>Your data is encrypted and protected</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon">📊</span>
                                <div>
                                    <h4>Powerful Analytics</h4>
                                    <p>Make data-driven decisions</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon">✨</span>
                                <div>
                                    <h4>Easy to Use</h4>
                                    <p>Intuitive interface designed for productivity</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="signup-card">
                    <div className="signup-header">
                        <div className="logo-container">
                            <Logo size={48} showText={true} />
                        </div>
                        <h2>Create Account</h2>
                        <p>Get started with your free account today</p>
                    </div>

                    {error && (
                        <div className="error-message">
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="signup-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">Full Name *</label>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        placeholder="John Doe"
                                        value={formData.name}
                                        onChange={handleChange}
                                        autoComplete="name"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="company">Company Name</label>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        placeholder="Your Company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        autoComplete="organization"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <div className="input-wrapper">
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="john@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="password">Password *</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        placeholder="Min. 6 characters"
                                        value={formData.password}
                                        onChange={handleChange}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password *</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        placeholder="Confirm password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex="-1"
                                    >
                                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="password-strength">
                            <div className="strength-bars">
                                <div className={`strength-bar ${formData.password.length > 0 ? 'active' : ''}`}></div>
                                <div className={`strength-bar ${formData.password.length >= 4 ? 'active' : ''}`}></div>
                                <div className={`strength-bar ${formData.password.length >= 6 ? 'active' : ''}`}></div>
                                <div className={`strength-bar ${formData.password.length >= 8 ? 'active' : ''}`}></div>
                            </div>
                            <span className="strength-text">
                                {formData.password.length === 0 ? 'Password strength' :
                                    formData.password.length < 4 ? 'Weak' :
                                        formData.password.length < 6 ? 'Fair' :
                                            formData.password.length < 8 ? 'Good' : 'Strong'}
                            </span>
                        </div>

                        <label className="terms-checkbox">
                            <input
                                type="checkbox"
                                checked={acceptTerms}
                                onChange={(e) => setAcceptTerms(e.target.checked)}
                            />
                            <span>
                                I agree to the{' '}
                                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                                {' '}and{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            </span>
                        </label>

                        <button type="submit" className="signup-button" disabled={loading}>
                            {loading ? (
                                <div className="spinner"></div>
                            ) : (
                                <span>Create Account</span>
                            )}
                        </button>

                        <div className="divider">
                            <span>or sign up with</span>
                        </div>

                        <div className="social-login">
                            <button
                                type="button"
                                className="social-button google"
                                onClick={handleGoogleSignup}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Google
                            </button>
                            <button
                                type="button"
                                className="social-button github"
                                onClick={handleGithubSignup}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                </svg>
                                GitHub
                            </button>
                        </div>
                    </form>

                    <div className="signup-footer">
                        <p>
                            Already have an account?{' '}
                            <Link to="/login" className="login-link">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUp;
